/**
 * AxisEditor - Edit single axis values
 *
 * Supports different value types: string, dimensions, number.
 * Includes color picker for color axes.
 * Uses visual grid selector for pattern axis.
 *
 * Design: V2 style - chips and inputs on the same row (inline).
 */

import React, { useState, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { VariantAxisConfig } from '../../../../../common/models/product-family.model';
import type { AxisValue, VariantV3 } from '../../types';
import {
  formatAxisValueForDisplay,
  isColorAxisValue,
  isDefaultAxisValue,
} from '../../types';
import { PatternSelector } from './PatternSelector';
import { VariantImpactDialog } from './VariantImpactDialog';
import { useResolveTranslation } from '../../../../../common/hooks/use-resolve-translation.hook';

// =============================================================================
// Types
// =============================================================================

interface AxisEditorProps {
  /** Axis configuration */
  config: VariantAxisConfig;
  /** Current values for this axis */
  values: AxisValue[];
  /** Change handler */
  onChange: (values: AxisValue[]) => void;
  /** Disabled state */
  disabled?: boolean;
  /** All variants (used to find affected variants when deleting axis values) */
  variants?: VariantV3[];
  /** Callback to delete variants by their UUIDs */
  onDeleteVariants?: (uuids: string[]) => void;
  /** Callback to assign a value to all variants with null for this axis */
  onAssignAxisValueToVariants?: (axisName: string, value: AxisValue) => void;
  /** Unit display from vendor config (e.g. 'cm', 'mm') */
  unitDisplay?: string;
}

/** Represents a pending deletion with the value and affected variants */
interface PendingDeletion {
  value: AxisValue;
  index: number;
  affectedVariants: VariantV3[];
}

/** Represents a pending assignment of a value to variants with null for this axis */
interface PendingAssignment {
  value: AxisValue;
  affectedVariants: VariantV3[];
}

// =============================================================================
// Local Components
// =============================================================================

/** Axis label + helper line — identical for the pattern grid and the inline editors. */
const AxisHeading: React.FC<{
  label: string;
  required?: boolean;
  helper: string;
  suffix?: React.ReactNode;
}> = ({ label, required, helper, suffix }) => (
  <div className="mb-3">
    <p className="text-xs font-medium text-muted-foreground">
      {label} {required && <span className="text-destructive">*</span>}
      {suffix}
    </p>
    <p className="text-xs text-muted-foreground/70">{helper}</p>
  </div>
);

/** One selected axis value. Rendered once per value, so the removal affordance stays uniform. */
const AxisValueChip: React.FC<{
  label: string;
  colorHex?: string;
  onRemove?: () => void;
}> = ({ label, colorHex, onRemove }) => {
  const { t } = useTranslation();

  return (
  <span
    data-slot="axis-value-chip"
    className="inline-flex h-8 items-center gap-1.5 border bg-muted/40 ps-2 pe-1 text-xs text-foreground"
  >
    {colorHex && (
      <span
        className="size-3.5 shrink-0 rounded-full border"
        style={{ backgroundColor: colorHex }}
      />
    )}
    <span>{label}</span>
    {onRemove && (
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={onRemove}
        aria-label={t('PAGES.NEW_PRODUCT.V3.AXES.REMOVE_VALUE', 'Remove value') as string}
        className="text-muted-foreground hover:text-destructive"
      >
        <X aria-hidden="true" />
      </Button>
    )}
  </span>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const AxisEditor: React.FC<AxisEditorProps> = ({
  config,
  values,
  onChange,
  disabled = false,
  variants = [],
  onDeleteVariants,
  onAssignAxisValueToVariants,
  unitDisplay,
}) => {
  const { t } = useTranslation();
  const { rt } = useResolveTranslation();

  // Input states
  const [inputValue, setInputValue] = useState('');
  const [colorHex, setColorHex] = useState('#000000');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState('');

  // Pending deletion state for confirmation dialog
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);

  // Pending assignment state for "assign to existing variants" dialog
  const [pendingAssignment, setPendingAssignment] = useState<PendingAssignment | null>(null);

  const isColorAxis = config.name === 'color';
  const isPatternAxis = config.name === 'pattern';
  const isDimensionAxis = config.valueType === 'dimensions';
  const is3D = config.dimensionCount === 3;

  // Find variants that use a specific axis value
  const getAffectedVariants = useCallback(
    (value: AxisValue): VariantV3[] => {
      const displayValue = formatAxisValueForDisplay(value);
      return variants.filter((variant) => {
        const variantAxisValue = variant.axes[config.name];
        if (variantAxisValue === undefined || variantAxisValue === null) return false;
        return formatAxisValueForDisplay(variantAxisValue) === displayValue;
      });
    },
    [variants, config.name]
  );

  // Find selected variants with null for this axis (for assignment popup)
  const getVariantsWithNullAxis = useCallback((): VariantV3[] => {
    return variants.filter(v => v.isSelected && isDefaultAxisValue(v.axes[config.name]));
  }, [variants, config.name]);

  const handleAddValue = useCallback(
    (newValue: AxisValue) => {
      // Check for duplicates
      const displayValue = formatAxisValueForDisplay(newValue);
      const existingDisplayValues = values.map(formatAxisValueForDisplay);
      if (existingDisplayValues.includes(displayValue)) {
        return; // Already exists
      }

      // Intercept: first value for an empty optional axis with existing variants
      if (
        values.length === 0 &&
        !config.required &&
        onAssignAxisValueToVariants
      ) {
        const nullVariants = getVariantsWithNullAxis();
        if (nullVariants.length > 0) {
          setPendingAssignment({ value: newValue, affectedVariants: nullVariants });
          return;
        }
      }

      onChange([...values, newValue]);
    },
    [values, onChange, config.required, onAssignAxisValueToVariants, getVariantsWithNullAxis]
  );

  // Request to remove a value - may show confirmation dialog if variants are affected
  const handleRemoveValue = useCallback(
    (index: number) => {
      const valueToRemove = values[index];
      const affectedVariants = getAffectedVariants(valueToRemove);

      if (affectedVariants.length > 0) {
        // Show confirmation dialog
        setPendingDeletion({
          value: valueToRemove,
          index,
          affectedVariants,
        });
      } else {
        // No variants affected, remove directly
        const newValues = [...values];
        newValues.splice(index, 1);
        onChange(newValues);
      }
    },
    [values, onChange, getAffectedVariants]
  );

  // Confirm deletion - removes the axis value AND deletes affected variants
  const handleConfirmDeletion = useCallback(() => {
    if (!pendingDeletion) return;

    // Delete affected variants first
    if (onDeleteVariants && pendingDeletion.affectedVariants.length > 0) {
      const uuidsToDelete = pendingDeletion.affectedVariants.map((v) => v.uuid);
      onDeleteVariants(uuidsToDelete);
    }

    // Then remove the axis value
    const newValues = [...values];
    newValues.splice(pendingDeletion.index, 1);
    onChange(newValues);

    // Close dialog
    setPendingDeletion(null);
  }, [pendingDeletion, values, onChange, onDeleteVariants]);

  // Cancel deletion
  const handleCancelDeletion = useCallback(() => {
    setPendingDeletion(null);
  }, []);

  // Confirm assignment - assigns the value to variants, then adds it to the axis
  const handleConfirmAssignment = useCallback(() => {
    if (!pendingAssignment) return;
    if (onAssignAxisValueToVariants) {
      onAssignAxisValueToVariants(config.name, pendingAssignment.value);
    }
    onChange([pendingAssignment.value]);
    setInputValue('');
    setColorHex('#000000');
    setWidth('');
    setHeight('');
    setDepth('');
    setPendingAssignment(null);
  }, [pendingAssignment, onAssignAxisValueToVariants, config.name, onChange]);

  // Cancel assignment
  const handleCancelAssignment = useCallback(() => {
    setPendingAssignment(null);
  }, []);

  // Handle add for dimensions
  const handleAddDimension = () => {
    const w = parseFloat(width);
    const h = parseFloat(height);
    const d = is3D ? parseFloat(depth) : undefined;

    if (isNaN(w) || isNaN(h) || (is3D && isNaN(d!))) {
      return;
    }

    handleAddValue({
      width: w,
      height: h,
      depth: is3D ? d : undefined,
      unit: unitDisplay || 'cm',
    });

    setWidth('');
    setHeight('');
    setDepth('');
  };

  // Handle add for colors
  const handleAddColor = () => {
    if (!inputValue.trim()) return;
    handleAddValue({ value: inputValue.trim(), hex: colorHex });
    setInputValue('');
    setColorHex('#000000');
  };

  // Handle add for strings
  const handleAddString = () => {
    if (!inputValue.trim()) return;
    handleAddValue(inputValue.trim());
    setInputValue('');
  };

  // Handle pattern selection change
  const handlePatternChange = useCallback(
    (patterns: string[]) => {
      onChange(patterns);
    },
    [onChange]
  );

  // Handle key press for Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (isDimensionAxis) {
        handleAddDimension();
      } else if (isColorAxis) {
        handleAddColor();
      } else {
        handleAddString();
      }
    }
  };

  // Get label text
  const configLabel = rt(config.label);
  const getLabelText = () => {
    if (isDimensionAxis) {
      const unit = unitDisplay || 'cm';
      return `${configLabel} (${unit})`;
    }
    return configLabel;
  };

  // Get contextual helper text based on axis type
  const getHelperText = () => {
    if (isColorAxis) return t('PAGES.NEW_PRODUCT.V3.AXES.HELPER_COLOR');
    if (isDimensionAxis) return t('PAGES.NEW_PRODUCT.V3.AXES.HELPER_SIZE');
    if (isPatternAxis) return t('PAGES.NEW_PRODUCT.V3.AXES.HELPER_PATTERN');
    return t('PAGES.NEW_PRODUCT.V3.AXES.HELPER_GENERIC');
  };

  const addButtonLabel = t('PAGES.NEW_PRODUCT.VARIANTS.ADD');

  // Pattern axis - use visual grid selector
  if (isPatternAxis && config.predefinedValues && config.predefinedValues.length > 0) {
    const selectedPatterns = values.filter((v): v is string => typeof v === 'string');

    return (
      <div>
        <AxisHeading
          label={getLabelText()}
          required={config.required}
          helper={getHelperText()}
          suffix={
            <span className="ms-1 font-normal text-muted-foreground">
              ({selectedPatterns.length} {t('SELECTED')})
            </span>
          }
        />

        {/* Pattern grid selector */}
        <PatternSelector
          availablePatterns={config.predefinedValues}
          selectedPatterns={selectedPatterns}
          onChange={handlePatternChange}
          disabled={disabled}
          variants={variants}
          onDeleteVariants={onDeleteVariants}
          onAssignAxisValueToVariants={onAssignAxisValueToVariants}
        />
      </div>
    );
  }

  return (
    <div>
      <AxisHeading label={getLabelText()} required={config.required} helper={getHelperText()} />

      {/* Chips + Input inline row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Existing value chips */}
        {values.map((value, index) => {
          const displayValue = formatAxisValueForDisplay(value);
          const colorHexValue = isColorAxisValue(value) ? value.hex : undefined;

          return (
            <AxisValueChip
              key={`${displayValue}-${index}`}
              label={displayValue}
              colorHex={colorHexValue}
              onRemove={disabled ? undefined : () => handleRemoveValue(index)}
            />
          );
        })}

        {/* Inline input based on value type */}
        {isDimensionAxis ? (
          // Dimension inputs
          <div className="flex items-center gap-2">
            <Input
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('PAGES.NEW_PRODUCT.VARIANTS.WIDTH')}
              type="number"
              disabled={disabled}
              className="w-[85px]"
            />
            <span className="text-xs text-muted-foreground">×</span>
            <Input
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.LENGTH')}
              type="number"
              disabled={disabled}
              className="w-[85px]"
            />
            {is3D && (
              <>
                <span className="text-xs text-muted-foreground">×</span>
                <Input
                  value={depth}
                  onChange={(e) => setDepth(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('PAGES.NEW_PRODUCT.VARIANTS.HEIGHT')}
                  type="number"
                  disabled={disabled}
                  className="w-[85px]"
                />
              </>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={handleAddDimension}
              disabled={disabled || !width || !height || (is3D && !depth)}
            >
              <Plus aria-hidden="true" />
              {addButtonLabel}
            </Button>
          </div>
        ) : isColorAxis ? (
          // Color input with picker
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={colorHex}
              onChange={(e) => setColorHex(e.target.value)}
              disabled={disabled}
              className={cn(
                'size-8 shrink-0 appearance-none border bg-transparent p-0',
                '[&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              )}
            />
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('PAGES.NEW_PRODUCT.VARIANTS.COLOR_NAME')}
              disabled={disabled}
              className="w-[140px]"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleAddColor}
              disabled={disabled || !inputValue.trim()}
            >
              <Plus aria-hidden="true" />
              {addButtonLabel}
            </Button>
          </div>
        ) : config.predefinedValues && config.predefinedValues.length > 0 && !config.allowCustomValues ? (
          // Select from predefined values
          <div className="flex items-center gap-2">
            <Select value={inputValue} onValueChange={setInputValue} disabled={disabled}>
              <SelectTrigger className="min-w-[160px]">
                <SelectValue placeholder={`${t('SELECT')} ${configLabel.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {config.predefinedValues.map((val) => (
                  <SelectItem key={val} value={val}>
                    {val}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddString}
              disabled={disabled || !inputValue}
            >
              <Plus aria-hidden="true" />
              {addButtonLabel}
            </Button>
          </div>
        ) : (
          // Text input for custom string values
          <div className="flex items-center gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('PAGES.NEW_PRODUCT.V3.AXES.INPUT_PLACEHOLDER', { axis: configLabel.toLowerCase() })}
              disabled={disabled}
              className="w-[160px]"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleAddString}
              disabled={disabled || !inputValue.trim()}
            >
              <Plus aria-hidden="true" />
              {addButtonLabel}
            </Button>
          </div>
        )}
      </div>

      {/* Help text for predefined suggestions */}
      {config.allowCustomValues && config.predefinedValues && config.predefinedValues.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('PAGES.NEW_PRODUCT.VARIANTS.SUGGESTED')}: {config.predefinedValues.slice(0, 5).join(', ')}
          {config.predefinedValues.length > 5 && '...'}
        </p>
      )}

      {/* Confirmation dialog for deleting axis values with affected variants */}
      <VariantImpactDialog
        open={!!pendingDeletion}
        onCancel={handleCancelDeletion}
        onConfirm={handleConfirmDeletion}
        destructive
        skipDefaultAxisValues
        variants={pendingDeletion?.affectedVariants || []}
        title={t('PAGES.NEW_PRODUCT.V3.DELETE_VALUE_TITLE', { defaultValue: 'Delete value?' })}
        message={t('PAGES.NEW_PRODUCT.V3.DELETE_VALUE_MESSAGE', {
          value: pendingDeletion ? formatAxisValueForDisplay(pendingDeletion.value) : '',
          count: pendingDeletion?.affectedVariants.length || 0,
          defaultValue: `Deleting "${pendingDeletion ? formatAxisValueForDisplay(pendingDeletion.value) : ''}" will also delete ${pendingDeletion?.affectedVariants.length || 0} variant(s):`,
        })}
        confirmLabel={t('DELETE', { defaultValue: 'Delete' })}
      />

      {/* Assignment dialog for assigning first value to variants with null axis */}
      <VariantImpactDialog
        open={!!pendingAssignment}
        onCancel={handleCancelAssignment}
        onConfirm={handleConfirmAssignment}
        skipDefaultAxisValues
        variants={pendingAssignment?.affectedVariants || []}
        title={t('PAGES.NEW_PRODUCT.V3.ASSIGN_VALUE_TITLE', { defaultValue: 'Assign to existing variants?' })}
        message={t('PAGES.NEW_PRODUCT.V3.ASSIGN_VALUE_MESSAGE', {
          count: pendingAssignment?.affectedVariants.length || 0,
          axis: configLabel,
          value: pendingAssignment ? formatAxisValueForDisplay(pendingAssignment.value) : '',
          defaultValue: `There are ${pendingAssignment?.affectedVariants.length || 0} existing variant(s) without a ${configLabel.toLowerCase()}. Do you want to assign "${pendingAssignment ? formatAxisValueForDisplay(pendingAssignment.value) : ''}" to them?`,
        })}
        confirmLabel={t('PAGES.NEW_PRODUCT.V3.ASSIGN_BUTTON', { defaultValue: 'Assign' })}
      />
    </div>
  );
};

export default AxisEditor;
